import {
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

export const DISMISS_DISTANCE = 120;
export const DISMISS_VELOCITY = 0.65;
/** Movement before a pull at the top of the content becomes a sheet drag. */
const PULL_SLOP = 6;

/** Whether a drag released `distance` px down after `elapsed` ms dismisses. */
export const shouldDismiss = (distance: number, elapsed: number): boolean =>
  distance > DISMISS_DISTANCE ||
  distance / Math.max(1, elapsed) > DISMISS_VELOCITY;

interface PageSheetDragOptions {
  sheetRef: RefObject<HTMLElement | null>;
  backdropRef: RefObject<HTMLElement | null>;
  scrollRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onDismiss: () => void;
}

interface PageSheetDragHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Swipe-down to dismiss a page sheet. The sheet follows the finger when
 * dragged by its header (pointer events), or when the content is pulled down
 * while already scrolled to its top (touch events, so normal scrolling is
 * untouched otherwise). The offset is written straight to the sheet's style
 * (no React render per frame); on release the inline style is dropped and the
 * CSS transition springs it back, or the sheet is dismissed.
 */
export const usePageSheetDrag = ({
  sheetRef,
  backdropRef,
  scrollRef,
  enabled,
  onDismiss,
}: PageSheetDragOptions): PageSheetDragHandlers => {
  const drag = useRef<{ origin: number; start: number } | null>(null);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  const move = (y: number) => {
    const sheet = sheetRef.current;
    if (!drag.current || !sheet) return;
    const offset = Math.max(0, y - drag.current.origin);
    sheet.style.transition = "none";
    sheet.style.transform = `translate3d(0, ${offset}px, 0)`;
    if (backdropRef.current) {
      backdropRef.current.style.transition = "none";
      backdropRef.current.style.opacity = String(
        Math.max(0, 1 - offset / Math.max(1, sheet.offsetHeight)),
      );
    }
  };

  const end = (y: number, timeStamp: number) => {
    const state = drag.current;
    if (!state) return;
    drag.current = null;
    for (const element of [sheetRef.current, backdropRef.current]) {
      element?.style.removeProperty("transition");
      element?.style.removeProperty("transform");
      element?.style.removeProperty("opacity");
    }
    if (shouldDismiss(Math.max(0, y - state.origin), timeStamp - state.start)) {
      dismissRef.current();
    }
  };

  const moveRef = useRef(move);
  const endRef = useRef(end);
  moveRef.current = move;
  endRef.current = end;

  // Pull down from the top of the content.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!enabled || !scroller) return;
    let pull: { y: number; armed: boolean } | null = null;

    const onTouchStart = (event: TouchEvent) => {
      pull =
        event.touches.length === 1 && scroller.scrollTop <= 0
          ? { y: event.touches[0].clientY, armed: true }
          : null;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (!pull?.armed) return;
      const y = event.touches[0].clientY;
      if (!drag.current) {
        const dy = y - pull.y;
        if (dy < 0 || scroller.scrollTop > 0) {
          // Scrolling the content up: leave it to the browser.
          pull = null;
          return;
        }
        if (dy < PULL_SLOP) return;
        drag.current = { origin: y, start: event.timeStamp };
      }
      if (event.cancelable) event.preventDefault();
      moveRef.current(y);
    };
    const onTouchEnd = (event: TouchEvent) => {
      pull = null;
      const touch = event.changedTouches[0];
      if (drag.current && touch) endRef.current(touch.clientY, event.timeStamp);
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    // Not passive: it must be able to stop the scroll once the sheet moves.
    scroller.addEventListener("touchmove", onTouchMove, { passive: false });
    scroller.addEventListener("touchend", onTouchEnd);
    scroller.addEventListener("touchcancel", onTouchEnd);
    return () => {
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchmove", onTouchMove);
      scroller.removeEventListener("touchend", onTouchEnd);
      scroller.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, scrollRef]);

  return {
    onPointerDown: (event) => {
      if (!enabled || event.button !== 0) return;
      if ((event.target as HTMLElement).closest("button")) return;
      drag.current = { origin: event.clientY, start: event.timeStamp };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    onPointerMove: (event) => move(event.clientY),
    onPointerUp: (event) => end(event.clientY, event.timeStamp),
    onPointerCancel: (event) => end(event.clientY, event.timeStamp),
  };
};
